import { Breadcrumbs as MuiBreadcrumbs, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { LuChevronRight } from 'react-icons/lu';
import AppIcon from '../../icons/app-icon';
import { breadcrumbEntityAtom } from './use-breadcrumb-entity';

/**
 * Maps known route segment keywords to human-readable labels. Segments that
 * aren't in this map and look like ids are skipped (or replaced by the active
 * entity name) so we never surface a raw id.
 */
const SEGMENT_LABELS: Record<string, string> = {
  events: 'Events',
  report: 'Dashboard',
  sessions: 'Sessions',
  answers: 'Answers',
  'create-session': 'New Session',
  'edit-session': 'Edit Session',
  theaters: 'Theaters',
  forms: 'Forms',
  'form-builder': 'Form Builder',
  users: 'Users',
  profile: 'Profile',
  settings: 'Settings',
  organizations: 'Organizations',
  whatsapp: 'WhatsApp',
  'super-admin': 'Platform control',
  guests: 'Guests',
  'whats-new': "What's New",
};

interface Crumb {
  label: string;
  /** Stable React key (the cumulative path). */
  key: string;
  /** Where the crumb navigates to, or null when it's the current page. */
  linkTo: string | null;
}

/** Title-cases an unknown (but non-id) segment as a graceful fallback. */
const titleCase = (segment: string) =>
  segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Treat a segment as a dynamic id (to skip / replace) when it carries a digit
// or is long.
const isDynamicId = (segment: string) =>
  /\d/.test(segment) || segment.length > 20;

/**
 * Builds the trail straight from the path segments — each top-level section
 * (Events, Forms, Users, …) is its OWN root, so nothing is forced under
 * "Events". The first id segment is replaced by the active entity name (event /
 * session / form) when one is registered, otherwise skipped. Every crumb except
 * the current page is clickable; deeper crumbs fall back to their section root
 * so a link is never broken.
 */
const buildCrumbs = (pathname: string, entityName: string | null): Crumb[] => {
  const segments = pathname.split('/').filter(Boolean);

  // The index route ("/") renders the Events page.
  if (segments.length === 0) {
    return [{ label: 'Events', key: '/', linkTo: null }];
  }

  const sectionRoot = `/${segments[0]}`;
  const crumbs: Crumb[] = [];
  let entityShown = false;

  segments.forEach((segment, index) => {
    const known = segment in SEGMENT_LABELS;
    const path = `/${segments.slice(0, index + 1).join('/')}`;

    if (!known && isDynamicId(segment)) {
      // Replace the first id with the entity name if we have one; else skip it.
      if (entityName && !entityShown) {
        entityShown = true;
        crumbs.push({ label: entityName, key: path, linkTo: sectionRoot });
      }
      return;
    }

    const label = SEGMENT_LABELS[segment] ?? titleCase(segment);
    const isTopLevel = path.split('/').filter(Boolean).length === 1;
    crumbs.push({ label, key: path, linkTo: isTopLevel ? path : sectionRoot });
  });

  // Safety net: never render an empty trail (e.g. a route that is only an id).
  if (crumbs.length === 0) {
    return [{ label: 'Events', key: '/', linkTo: null }];
  }

  // The last crumb is the current page — not a link.
  crumbs[crumbs.length - 1].linkTo = null;
  return crumbs;
};

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const entityName = useAtomValue(breadcrumbEntityAtom);
  const crumbs = buildCrumbs(pathname, entityName);

  return (
    <MuiBreadcrumbs
      aria-label='breadcrumb'
      separator={
        <AppIcon icon={LuChevronRight} size={16} color='text.disabled' />
      }
      sx={{
        fontSize: '0.9rem',
        '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap', rowGap: 0.5 },
        '& .MuiBreadcrumbs-separator': { mx: 0.75 },
      }}
    >
      {crumbs.map((crumb) => {
        if (!crumb.linkTo) {
          return (
            <Typography
              key={crumb.key}
              aria-current='page'
              sx={{
                fontSize: 'inherit',
                fontWeight: 700,
                color: 'text.primary',
                maxWidth: { xs: 200, sm: 360 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {crumb.label}
            </Typography>
          );
        }

        return (
          <MuiLink
            key={crumb.key}
            component={RouterLink}
            to={crumb.linkTo}
            underline='hover'
            sx={{
              fontSize: 'inherit',
              fontWeight: 500,
              color: 'text.secondary',
              transition: 'color 0.15s ease',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {crumb.label}
          </MuiLink>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
